if (!customElements.get('wls-s-product-form')) {
  class WLS_S_ProductForm extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        inputQuantity: 'input[name="quantity"]',
        inputVariantId: 'input[name="id"]',
        inputSellingPlan: 'input[name="selling_plan"]',
        inputPurchaseType: 'input[name="purchase-type"]',
        productFormFrequency: 'wls-s-product-form-frequency',
        totalPrice: 'js-total-price',
        totalCompareAtPrice: 'js-total-compare-at-price',
        frequencyPrice: 'js-frequency-price',
        frequencyCompareAtPrice: 'js-frequency-compare-at-price',
        loading: 'loading',
        submitButton: 'button[type="submit"]',
        errorMessage: 'js-error-message',
      };

      this.purchaseType = {
        OTP: 'one-time',
        SUB: 'subscription',
      };

      this.boundHandleInputChange = this.handleInputChange.bind(this);
      this.boundHandleSubmit = this.handleSubmit.bind(this);
      this.boundHandleMutation = this.handleMutation.bind(this);
    }

    get selectors() {
      return {
        form: this.querySelector('form'),
        quantityInput: this.querySelector(this.classes.inputQuantity),
        variantIdInput: this.querySelector(this.classes.inputVariantId),
        sellingPlanInput: this.querySelector(this.classes.inputSellingPlan),
        purchaseTypeInputs: this.querySelectorAll(this.classes.inputPurchaseType),
        productFormFrequency: this.querySelector(this.classes.productFormFrequency),
        totalPrice: this.querySelector(`.${this.classes.totalPrice}`),
        totalCompareAtPrice: this.querySelector(`.${this.classes.totalCompareAtPrice}`),
        frequencyPrice: this.querySelector(`.${this.classes.frequencyPrice}`),
        frequencyCompareAtPrice: this.querySelector(`.${this.classes.frequencyCompareAtPrice}`),
        submitButton: this.querySelector(this.classes.submitButton),
        errorMessage: this.querySelector(`.${this.classes.errorMessage}`),
        cart: document.querySelector('cart-notification') || document.querySelector('cart-drawer'),
      };
    }

    get dataAttributes() {
      return {
        blockId: this.getAttribute('data-block-id'),
        sectionId: this.getAttribute('data-section-id'),
        productForm: this.getAttribute('data-product-form'),
        formDisabled: this.getAttribute('data-form-disabled'),
        initPurchaseType: this.selectors.productFormFrequency?.getAttribute('data-init-purchase-type') || 'one-time',
      };
    }

    get inputsWithEvents() {
      return [
        { element: this.selectors.quantityInput, events: ['change', 'input'] },
        { element: this.selectors.variantIdInput, events: ['change', 'input'] },
        { element: this.selectors.sellingPlanInput, events: ['change', 'input'] },
        { element: this.selectors.purchaseTypeInputs, events: ['change', 'input'] },
      ];
    }

    get currentProductVariantObject() {
      return window.productVariants?.find((variant) => variant.id.toString() === this.currentFormState.variantId);
    }

    connectedCallback() {
      this.initializeFormState();
      this.bindEvents();
      this.setupMutationObserver();
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.disconnectMutationObserver();
    }

    initializeFormState() {
      this.currentFormState = {
        quantity: this.selectors.quantityInput?.value || 1,
        variantId: this.selectors.variantIdInput?.value || '',
        sellingPlan: this.selectors.sellingPlanInput?.value || '',
        purchaseType: this.dataAttributes.initPurchaseType || '',
      };
    }

    bindEvents() {
      this.inputsWithEvents.forEach(({ element, events }) => {
        if (element) {
          // Check if element is a NodeList (array-like) or single element
          const elements = element.length !== undefined ? Array.from(element) : [element];
          elements.forEach((el) => {
            if (el) {
              events.forEach((event) => {
                el.addEventListener(event, this.boundHandleInputChange);
              });
            }
          });
        }
      });

      if (this.selectors.form) {
        this.selectors.form.addEventListener('submit', this.boundHandleSubmit);
      }
    }

    unbindEvents() {
      this.inputsWithEvents.forEach(({ element, events }) => {
        if (element) {
          // Check if element is a NodeList (array-like) or single element
          const elements = element.length !== undefined ? Array.from(element) : [element];
          elements.forEach((el) => {
            if (el) {
              events.forEach((event) => {
                el.removeEventListener(event, this.boundHandleInputChange);
              });
            }
          });
        }
      });

      if (this.selectors.form) {
        this.selectors.form.removeEventListener('submit', this.boundHandleSubmit);
      }
    }

    handleInputChange(e) {
      const input = e.target;
      const inputName = input.name;
      const inputValue = input.value;

      // Update the currentFormState based on input name
      this.updateCurrentState(inputName, inputValue);
    }

    updateCurrentState(fieldName, value) {
      const { frequencyPrice, frequencyCompareAtPrice, totalPrice, totalCompareAtPrice } = this.selectors;
      switch (fieldName) {
        case 'quantity':
          this.currentFormState.quantity = parseInt(value) || 1;
          this.updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          break;
        case 'id':
          this.currentFormState.variantId = value;
          this.updatePrices({
            priceElement: frequencyPrice,
            compareAtElement: frequencyCompareAtPrice,
          });
          this.updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          this.currentProductVariantObject;
          break;
        case 'selling_plan':
          this.currentFormState.sellingPlan = value;
          this.updatePrices({
            priceElement: frequencyPrice,
            compareAtElement: frequencyCompareAtPrice,
          });
          this.updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          break;
        case 'purchase-type':
          this.currentFormState.purchaseType = value;
          this.updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          break;
      }
    }

    updatePrices({ priceElement, compareAtElement, multiplier = 1 }) {
      const variant = this.currentProductVariantObject;
      const { purchaseType, sellingPlan } = this.currentFormState;

      if (!variant || !priceElement) return;

      const isSubscription = purchaseType === this.purchaseType.SUB;
      const allocation = isSubscription
        ? variant.selling_plan_allocations.find((a) => a.selling_plan_id.toString() === sellingPlan)
        : null;

      const toInt = (x) => (x == null ? null : Number(x));
      const price = toInt(allocation?.price ?? variant.price);
      const compareAt = toInt(allocation?.compare_at_price ?? variant.compare_at_price);

      priceElement.textContent = this.formatMoney(price * multiplier);

      if (compareAtElement) {
        if (compareAt) compareAtElement.textContent = this.formatMoney(compareAt * multiplier);
        else compareAtElement.textContent = '';
      }
    }

    async handleSubmit(e) {
      e.preventDefault();
      if (this.dataAttributes.formDisabled) return;

      try {
        this.addFormLoadingState();

        const item = this.buildCartItem();

        if (!item.id) throw new Error('Missing variant id');

        this.selectors.cart.setActiveElement(document.activeElement);

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [item],
            sections: this.selectors.cart.getSectionsToRender().map((section) => section?.section),
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

        if (!response.ok) {
          // Try to read the JSON error from Shopify
          let message = `Cart add failed (${response.status})`;
          try {
            const err = await response.json();
            if (err?.description) message = err.description;
            if (err?.message) message = err.message;
          } catch (_) {}
          this.renderErrorMessage(data.errors);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'wls-s-buybox',
          productVariantId: item.id,
          cartData: data,
        }).then(() => {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        });
        CartPerformance.measure('add:paint-updated-sections', () => {
          this.selectors.cart.renderContents(data);
        });
      } catch (error) {
        console.error('[WLS_S_ProductForm] Add to cart error:', error);
      } finally {
        this.removeFormLoadingState();
        if (this.selectors.cart && this.selectors.cart.classList.contains('is-empty'))
          this.selectors.cart.classList.remove('is-empty');

        CartPerformance.measureFromEvent('add:user-action', e);
      }
    }

    getLineItemPropertiesFromForm() {
      const form = this.selectors.form;
      if (!form) return null;
      const props = {};
      const fd = new FormData(form);

      for (const [key, value] of fd.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && m[1]) props[m[1]] = value;
      }
      return Object.keys(props).length ? props : null;
    }

    buildCartItem() {
      const { variantId, quantity, purchaseType, sellingPlan } = this.currentFormState || {};
      const item = {
        id: Number(variantId),
        quantity: Number(quantity) || 1,
      };

      if (purchaseType === this.purchaseType.SUB && sellingPlan) {
        item.selling_plan = String(sellingPlan);
      }

      const props = this.getLineItemPropertiesFromForm();
      if (props) item.properties = props;

      return item;
    }

    setupMutationObserver() {
      // Create observer to watch for attribute changes on inputs
      this.mutationObserver = new MutationObserver(this.boundHandleMutation);

      // Start observing all input elements for attribute changes
      const inputs = this.inputsWithEvents;
      inputs.forEach(({ element }) => {
        if (element) {
          // Check if element is a NodeList (array-like) or single element
          const elements = element instanceof NodeList ? [...element] : [element];
          elements.forEach((el) => {
            if (el) {
              this.mutationObserver.observe(el, {
                attributes: true,
                attributeFilter: ['value'],
                subtree: false,
              });
            }
          });
        }
      });
    }

    renderErrorMessage(message) {
      this.selectors.errorMessage.textContent = message;

      setTimeout(() => {
        this.selectors.errorMessage.textContent = '';
      }, 5000);
    }

    addFormLoadingState() {
      this.selectors.submitButton.classList.add(this.classes.loading);
    }

    removeFormLoadingState() {
      this.selectors.submitButton.classList.remove(this.classes.loading);
    }

    handleMutation(mutations) {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const input = mutation.target;
          const inputName = input.name;
          const inputValue = input.value;

          // Update currentFormState when value attribute changes
          this.updateCurrentState(inputName, inputValue);
        }
      });
    }

    formatMoney(cents) {
      const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
      return formatMoney(cents, moneyFormat) || `$${(cents / 100).toFixed(2)}`;
    }

    disconnectMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    }
  }

  customElements.define('wls-s-product-form', WLS_S_ProductForm);
}
